/** @jsxImportSource theme-ui */

import React from 'react';
import { useCheckboxGroupItem } from '@react-aria/checkbox';
import { useToggleState } from '@react-stately/toggle';
import { useFocusRing } from '@react-aria/focus';
import { CheckboxGroupContextState, useCheckboxGroup } from './CheckboxGroup';
import { AriaCheckboxProps } from '@react-types/checkbox';
import { VisuallyHidden } from '@react-aria/visually-hidden';
import { getVariantOrBase } from '../utils';
import { Icon } from '../Icon';
import { CarrotTheme } from '../../theme';
import { ErrorProps } from '../utils/types/error';
import { FlexContainer, Text } from '../..';

export type CheckboxInputVariants =
    | 'error'
    | 'disabled'
    | 'selected'
    | 'selected-disabled'
    | 'focus'
    | 'selected-focus'
    | 'error-focus';
export interface CheckboxProps extends AriaCheckboxProps, ErrorProps {
    value: string;
    variant?: CheckboxInputVariants;
    secondaryText?: string;
    /** Controlled state of a Checkbox not in a CheckboxGroup */
    isChecked?: boolean;
}

function calculateVariant({
    variant,
    isChecked,
    isDisabled,
    isError,
    isFocusVisible,
}: Pick<CheckboxProps, 'variant' | 'isDisabled' | 'isError' | 'isChecked'> & {
    isFocusVisible: boolean;
}): CheckboxInputVariants {
    const invalid = isError;
    if (isChecked && isDisabled) {
        return 'selected-disabled';
    }
    if (isChecked && isFocusVisible) {
        return 'selected-focus';
    }
    if (invalid && isFocusVisible) {
        return 'error-focus';
    }
    if (isChecked) {
        return 'selected';
    }
    if (isDisabled) {
        return 'disabled';
    }
    if (isFocusVisible) {
        return 'focus';
    }
    if (invalid) {
        return 'error';
    }
    return variant;
}

type FilterNonInputPropsArgs = CheckboxProps & CheckboxGroupContextState;
type FilterNonInputPropsReturn = Omit<
    FilterNonInputPropsArgs,
    | 'isError'
    | 'children'
    | 'isIndeterminate'
    | 'secondaryText'
    | 'isDisabled'
    | 'isSelected'
    | 'toggleValue'
    | 'removeValue'
    | 'setValue'
    | 'addValue'
    | 'isReadOnly'
    | 'defaultSelected'
    | 'isChecked'
>;

function filterNonInputProps(props: FilterNonInputPropsArgs): FilterNonInputPropsReturn {
    /* eslint-disable @typescript-eslint/no-unused-vars */
    const {
        isError,
        children,
        isIndeterminate,
        secondaryText,
        isDisabled,
        isSelected,
        defaultSelected,
        toggleValue,
        removeValue,
        setValue,
        addValue,
        isChecked,
        isReadOnly,
        ...otherProps
    } = props;
    /* eslint-enable @typescript-eslint/no-unused-vars */
    return otherProps;
}

export function Checkbox(props: CheckboxProps): JSX.Element {
    const contextState = useCheckboxGroup();
    const toggleState = useToggleState({
        ...props,
        isSelected: (contextState?.isSelected && contextState?.isSelected(props.value)) || props.isChecked,
    });
    const state = {
        ...props,
        isSelected: () => toggleState.isSelected,
        removeValue: () => toggleState.setSelected(false),
        addValue: () => toggleState.setSelected(true),
        ...contextState,
    };
    const ref = React.useRef();
    const { inputProps } = useCheckboxGroupItem(props, state, ref);

    const { isError, children, isIndeterminate, secondaryText, isSelected } = state;
    const { isFocusVisible, focusProps } = useFocusRing();
    const variant = calculateVariant({
        isChecked: isSelected(props.value),
        isDisabled: props.isDisabled,
        isError,
        isFocusVisible,
    });

    return (
        <label sx={{ variant: getVariantOrBase('forms.checkbox-label', variant) }}>
            <VisuallyHidden>
                {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment*/}
                {/* @ts-ignore */}
                <input
                    {...filterNonInputProps(state as FilterNonInputPropsArgs)}
                    {...inputProps}
                    {...focusProps}
                    type="checkbox"
                    ref={ref}
                    disabled={props.isDisabled}
                    name={state?.name}
                />
            </VisuallyHidden>
            <div
                sx={{
                    variant: getVariantOrBase('forms.checkbox', variant),
                }}
            >
                {isSelected(props.value) &&
                    (isIndeterminate ? (
                        <span
                            sx={{
                                height: '2px',
                                width: '14px',
                                backgroundColor: 'white',
                            }}
                        ></span>
                    ) : (
                        <Icon name="checkmark" color="white" />
                    ))}
            </div>
            <FlexContainer direction="column" justifyContent="flex-start">
                {children}
                {secondaryText && (
                    <Text size="medium" sx={{ color: (theme: CarrotTheme) => theme.colors.black40 }}>
                        {secondaryText}
                    </Text>
                )}
            </FlexContainer>
        </label>
    );
}

Checkbox.defaultProps = {
    onChange: () => {
        /* do nothing */
    },
};

export * from './CheckboxGroup';
